import {
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { useT } from '../i18n/useT.js';
import type { MessageKey } from '../i18n/da.js';
import { InvoiceDraftForm } from './InvoiceDraftForm.js';
import { InvoiceDetailModal } from './InvoiceDetailModal.js';
import { formatDate, formatMoney } from '../format.js';
import { useInvoices } from '../api/hooks.js';
import type { Invoice } from '../api/hooks.js';

/**
 * Fakturaer.
 *
 * Two things this page must not soften, both of which a friendlier wording
 * would quietly break:
 *
 * 1. **Issuing is irreversible.** It allocates a number from a run Danish law
 *    requires to be unbroken and posts to an immutable ledger, in one
 *    transaction. After it the invoice cannot be edited or deleted; a mistake
 *    is corrected by credit note. The confirm step says exactly that.
 *
 * 2. **Sending currently transmits nothing.** The dry-run provider returns
 *    `status: sent` with `delivered: false`. This page shows **delivered**, not
 *    status — reporting "sent" would tell someone an invoice reached a customer
 *    when no socket was ever opened.
 */

const STATUS_LABELS: Record<Invoice['status'], MessageKey> = {
  draft: 'invoices.status.draft',
  sent: 'invoices.status.sent',
  received: 'invoices.status.received',
  paid: 'invoices.status.paid',
  overdue: 'invoices.status.overdue',
  cancelled: 'invoices.status.cancelled',
};

const STATUS_COLORS: Record<Invoice['status'], string> = {
  draft: 'gray',
  sent: 'blue',
  received: 'blue',
  paid: 'green',
  overdue: 'red',
  cancelled: 'dark',
};

export function InvoicesPage(): ReactNode {
  const t = useT();
  const [status, setStatus] = useState<string>('alle');
  const [selected, setSelected] = useState<Invoice | null>(null);
  const [draftOpened, draftForm] = useDisclosure(false);
  const [detailOpened, detailModal] = useDisclosure(false);

  const invoices = useInvoices(
    status === 'alle' ? {} : { status: status as Invoice['status'] },
  );

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center" wrap="wrap">
        <Title order={2}>{t('invoices.title')}</Title>
        <Button onClick={draftForm.open}>{t('invoices.newDraft')}</Button>
      </Group>

      <SegmentedControl
        value={status}
        onChange={setStatus}
        aria-label={t('invoices.filterLabel')}
        data={[
          { value: 'alle', label: t('invoices.filter.all') },
          { value: 'draft', label: t('invoices.filter.drafts') },
          { value: 'sent', label: t('invoices.filter.sent') },
          { value: 'paid', label: t('invoices.filter.paid') },
        ]}
      />

      {invoices.isPending && (
        <Center p="xl">
          <Loader />
        </Center>
      )}

      {invoices.isError && <ErrorMessage error={invoices.error} />}

      {invoices.isSuccess && invoices.data.items.length === 0 && (
        <Text c="dimmed">{t('invoices.none')}</Text>
      )}

      {invoices.isSuccess && invoices.data.items.length > 0 && (
        <Table.ScrollContainer minWidth={780}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 130 }}>{t('invoices.col.number')}</Table.Th>
                <Table.Th style={{ width: 110 }}>{t('invoices.col.date')}</Table.Th>
                <Table.Th>{t('invoices.col.customer')}</Table.Th>
                <Table.Th style={{ width: 110 }}>{t('invoices.col.status')}</Table.Th>
                <Table.Th style={{ width: 130 }} className="money-cell">
                  {t('invoices.col.total')}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {invoices.data.items.map((invoice) => (
                <Table.Tr key={invoice.id}>
                  <Table.Td>
                    {/*
                      A draft has no number, and saying so is more honest than
                      an em dash: the number does not exist yet rather than
                      being unknown. It is allocated at issue precisely so a
                      deleted draft cannot leave a hole in the run.
                    */}
                    {invoice.number === null ? (
                      <Text size="sm" c="dimmed">
                        {t('invoices.noNumberYet')}
                      </Text>
                    ) : (
                      <Text ff="monospace">{invoice.number}</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatDate(invoice.date)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{invoice.contactName ?? ''}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={STATUS_COLORS[invoice.status]}
                      variant="light"
                      size="sm"
                    >
                      {t(STATUS_LABELS[invoice.status])}
                    </Badge>
                  </Table.Td>
                  <Table.Td className="money-cell">{formatMoney(invoice.total)}</Table.Td>
                  <Table.Td>
                    <Button
                      size="compact-sm"
                      variant="subtle"
                      onClick={() => {
                        setSelected(invoice);
                        detailModal.open();
                      }}
                    >
                      {t('invoices.open')}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <Alert color="gray" variant="light" title={t('invoices.numbersTitle')}>
        {t('invoices.numbers')}
      </Alert>

      <InvoiceDraftForm opened={draftOpened} onClose={draftForm.close} />
      <InvoiceDetailModal
        invoice={selected}
        opened={detailOpened}
        onClose={() => {
          detailModal.close();
          setSelected(null);
        }}
      />
    </Stack>
  );
}
